<template>
  <teleport to="body">
    <transition name="lightbox">
      <div
        v-if="isOpen"
        ref="dialog"
        class="lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="t('Screenshots')"
      >
        <!-- The bar is the window's chrome, in the shape BrowserFrame's is: where
             you are on the left, what you can do on the right, and never a
             control over the picture itself.

             The counter is the one thing a viewer of a set needs that a single
             picture does not: how many there are and which one this is. It comes
             first because it is the address of the run, the way the framed site's
             address is the first thing in its bar.

             The three buttons are the whole of the navigation, and they are here
             rather than floating over the picture's edges because the screenshot
             is the content: an arrow laid over it covers the very pixels the
             reader opened the viewer to see. Two of them are the run's ends and
             the third is the way out, in the order a reader's hand expects, with
             the close at the far end of the row. -->
        <div class="lightbox-bar">
          <p class="lightbox-count">{{ modelValue + 1 }} / {{ images.length }}</p>

          <div class="lightbox-actions">
            <button
              class="lightbox-btn"
              type="button"
              :disabled="!hasPrevious"
              :aria-label="t('Previous screenshot')"
              @click="previous"
            >
              <font-awesome-icon icon="chevron-left" aria-hidden="true" />
            </button>

            <button
              ref="close"
              class="lightbox-btn"
              type="button"
              :aria-label="t('Close')"
              @click="close"
            >
              <font-awesome-icon icon="times" aria-hidden="true" />
            </button>

            <button
              class="lightbox-btn"
              type="button"
              :disabled="!hasNext"
              :aria-label="t('Next screenshot')"
              @click="next"
            >
              <font-awesome-icon icon="chevron-right" aria-hidden="true" />
            </button>
          </div>
        </div>

        <!-- The stage, and the whole of the picture's own behaviour: click the
             ground around the shot and the window closes, the way a viewer does.

             The ground is two boxes rather than one because the picture is
             centred rather than stretched. The stage is the air inside the
             window's padding, and the frame is the box the shot is fitted into;
             the shot is centred in the frame, so the frame's own edges hold the
             letterboxing a narrower picture leaves beside it. Both of them are
             ground, so both close, and `self` is what tells a click on the
             picture from a click on the space it is sitting in. -->
        <div class="lightbox-stage" @click.self="close">
          <figure class="lightbox-figure">
            <div class="lightbox-frame" @click.self="close">
              <img class="lightbox-image" :src="current.src" :alt="current.caption" />
            </div>

            <!-- The caption the case page already prints under the crop, carried
                 into the viewer so the reader does not lose the sentence that
                 says what they are looking at when they open it up. -->
            <figcaption v-if="current.caption" class="lightbox-caption">
              {{ current.caption }}
            </figcaption>
          </figure>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* A picture, opened full size over the page.

   The case page frames each screenshot in a crop, which is right for reading
   the story and wrong for looking at the work: a 1440-wide capture inside a
   column is a thumbnail. This is the one place on the site a capture is shown
   at its own size, and it is the owner of that job rather than a rule inside the
   chapter, because the reader leaves the page's flow to see it and comes back.

   The ground is `$ground-black`, the one sheet colour the site lays over a
   picture: the frame's lock guard and its hover zoom scrim take the same value.
   It is near-opaque rather than solid so a sliver of the page stays visible
   around the edges and the viewer reads as a layer above the story rather than
   as a new page the reader has navigated to.

   The strength is also what the white type in its bar is sitting on. The
   counter, the caption and the three controls are all on-dark steps, so the
   sheet has to stay this close to black for them to be read: at .95 it
   composites to about rgb(13 13 13) over a white page, and a sheet light enough
   to need seeing through would take the caption's contrast with it.

   The z-index is above the header and above the chat card, because a viewer
   opened from the middle of the page has to be the topmost thing on it, and both
   of those are fixed and would otherwise paint over its top bar. */
.lightbox {
  background: rgba($ground-black, .95);
  display: flex;
  flex-direction: column;
  inset: 0;
  position: fixed;
  z-index: 1200;
}

/* The bar. The same rhythm as the framed site's: it is chrome, so it takes the
   page's own paddings and keeps the picture below it rather than around it. */
.lightbox-bar {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}

/* A counter is a machine's address, so it is set the way the frame sets its own.
   It takes the softer white because it is a position in a set rather than
   something the reader reads, and it never wraps: "2 / 5" broken across two
   lines in a bar that has room for it would be worse than the space it saves. */
.lightbox-count {
  color: $on-dark-body;
  font-family: $font-mono;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  margin: 0;
  white-space: nowrap;
}

.lightbox-actions {
  display: flex;
  gap: 0.5rem;
}

/* The controls. `_reset.scss` strips a button's background, border and outline,
   so a control has to draw itself; this is that drawing, and it is the same
   square for all three so the row reads as one set of controls rather than as
   two arrows and a close.

   The disabled ends are dimmed rather than removed, because an arrow that
   disappears at the end of the run tells the reader nothing about whether there
   are more pictures, while a dimmed one says the run ends here. `cursor:
   not-allowed` is deliberately not set: the site does not use it, and a control
   that is simply unavailable should not appear to be refusing the reader. */
.lightbox-btn {
  align-items: center;
  background: $on-dark-fill;
  border: 1px solid $on-dark-line;
  border-radius: $r-sm;
  color: $on-dark-strong;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  transition: background-color $dur-quick $ease, border-color $dur-quick $ease;
  width: 2.5rem;

  svg {
    font-size: 0.85rem;
  }

  &:hover:not([disabled]),
  &:focus-visible {
    background: $on-dark-fill-strong;
    border-color: $on-dark-line-strong;
  }

  /* The site's ring, at an inset offset: the bar is inside a fixed sheet and a
     ring that hung outside these controls would have nothing to sit on. */
  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: -3px;
  }

  &[disabled] {
    opacity: 0.35;
  }
}

/* The stage owns the space the picture has, and nothing else. `min-height: 0` is
   what lets it hand a real height down to the shot rather than growing with it,
   which is the whole of the fitting below. */
.lightbox-stage {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 1.25rem 1.5rem;
}

.lightbox-figure {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.85rem;
  margin: 0;
  min-height: 0;
  min-width: 0;
}

/* The box the picture is fitted into. The chain of `flex: 1 1 auto` and
   `min-height: 0` from the fixed sheet down to here is what gives this box a
   definite height, and a definite height is the only thing a percentage
   `max-height` can resolve against. Without it the shot would fall back to its
   own pixels and a 1440-wide capture would run off the bottom of the screen. */
.lightbox-frame {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  min-height: 0;
}

/* The picture, at its own proportions and never stretched. It is capped on both
   axes and keeps its aspect ratio, which is what "full size" means for a
   capture this shape: as large as the window allows, whole. `object-fit` is
   restated because `_reset.scss` sets `cover` on every image, and a viewer that
   cropped the shot would be showing the reader less than the thumbnail did. */
.lightbox-image {
  border-radius: $r-sm;
  display: block;
  height: auto;
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  width: auto;
}

/* The sentence under the shot, at the caption scale the case page's prose
   already uses for the same words. It is centred against a picture it is
   narrower than, and capped so a long caption stays a caption rather than
   becoming a full-width line under a wide image. */
.lightbox-caption {
  color: $on-dark-body;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 auto;
  max-width: 68ch;
  text-align: center;
}

/* The sheet fades in and out on the site's own duration, the way the frame's
   guard does: it is the same kind of event, a layer arriving over the page. */
.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity $dur $ease;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

/* Below lg the caption can take the width it is given, and the stage keeps a
   little less air at its edges so the shot is as large as the screen allows. */
@media (max-width: 991.98px) {
  .lightbox-stage {
    padding: 0 1rem 1.25rem;
  }

  .lightbox-caption {
    max-width: none;
  }
}
</style>

<script>
import { faChevronLeft, faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';

FontAwesomeLibrary.add(faChevronLeft, faChevronRight, faTimes);

/* What the focus trap walks. It asks for the buttons that are available rather
   than for everything focusable, because the two arrows at the ends of the run
   are `disabled` and a trap that stepped onto them would hand the keyboard to
   controls that cannot do anything. */
const FOCUSABLE = 'button:not([disabled])';

export default {
  props: {
    /* The shots the run holds, in the order the viewer offers them. Each one is
       the picture and the sentence that describes it, already in the reader's
       language: the page that knows which screenshot belongs to which chapter is
       the page that resolves the caption. */
    images: {
      type: Array,
      default: () => []
    },
    /* Which shot is open, or null when the viewer is closed.

       This is the viewer's whole state, and it is one value rather than an
       `open` flag plus an index because a viewer open on nothing is not a state.
       That also makes the page's own state one number: the trigger sets it to a
       position in the run, and closing sets it back to null. */
    modelValue: {
      type: Number,
      default: null
    }
  },

  emits: ['update:modelValue'],

  setup() {
    /* The viewer's own copy, declared here for the same reason BrowserFrame
       declares its guard's: the chrome belongs to the component, so a caller
       that opens a set of pictures does not have to translate the controls that
       open it. The dialog's own name is here too, because a screen reader
       announces the window before it announces anything in it. */
    const { t } = useI18n({
      messages: {
        'en-MY': {
          Close: 'Close',
          'Next screenshot': 'Next screenshot',
          'Previous screenshot': 'Previous screenshot',
          Screenshots: 'Screenshots'
        },
        'zh-MY': {
          Close: '关闭',
          'Next screenshot': '下一张截图',
          'Previous screenshot': '上一张截图',
          Screenshots: '截图'
        },
        'ms-MY': {
          Close: 'Tutup',
          'Next screenshot': 'Tangkapan skrin seterusnya',
          'Previous screenshot': 'Tangkapan skrin sebelumnya',
          Screenshots: 'Tangkapan skrin'
        }
      }
    });

    return { t };
  },

  computed: {
    /* Open only when there is a position and a run to look in. A page that
       passes an empty set is a viewer with nothing to show, and one that passes
       a position into a set it has since lost would be a window on nothing. */
    isOpen() {
      return this.modelValue !== null && this.modelValue >= 0 && this.images.length > 0;
    },

    current() {
      return this.images[this.modelValue] || { src: '', caption: '' };
    },

    /* The ends of the run. The arrows are dimmed at them rather than wrapped
       around, because a set has a beginning and an ending and a control that
       silently jumped from the last shot to the first would say otherwise.

       Both read through `isOpen` as well as through the position, so a viewer
       that is closed answers `false` to both rather than treating its own null
       as the first shot: `null` compares as zero, and an arrow that was live
       while the window was shut would be a control with nothing behind it. */
    hasPrevious() {
      return this.isOpen && this.modelValue > 0;
    },

    hasNext() {
      return this.isOpen && this.modelValue < this.images.length - 1;
    }
  },

  watch: {
    /* The page's scroll and the keyboard belong to whichever layer is on top:
       while the window is over the page the page behind it must not move, and
       Escape must close the viewer rather than reach the page. Both are taken
       here and both are given back, which is why they are one watcher on the
       single value that says which layer is on top. */
    isOpen: {
      immediate: true,
      handler(open) {
        if (open) {
          this.enter();
        } else {
          this.leave();
        }
      }
    }
  },

  beforeUnmount() {
    /* An Inertia navigation can unmount the page with the viewer still open, and
       a listener or a locked scroll left behind outlives the component: the next
       page would open with its own scroll frozen and Escape doing nothing. */
    this.leave();
  },

  /* The three values the viewer holds while it is open, and why none is in
     `data`. All are instance properties rather than reactive state: one is a DOM
     node, one is a number and one is a CSS string, none is ever read by the
     template, and putting the focused element in `data` would deep-proxy it.
     They are left unset rather than initialised, which is what makes `leave`
     able to tell "the viewer never opened" from "the viewer is closing": nothing
     is handed back that was never taken. */

  methods: {
    close() {
      this.$emit('update:modelValue', null);
    },

    /* Navigation is clamped rather than wrapped for the same reason the arrows
       are dimmed at the ends: the handler and the control have to agree about
       where the run stops, and a keyboard that could wrap where a button could
       not would be two answers to one question. */
    go(index) {
      if (index < 0 || index >= this.images.length) {
        return;
      }

      this.$emit('update:modelValue', index);
    },

    previous() {
      this.go(this.modelValue - 1);
    },

    next() {
      this.go(this.modelValue + 1);
    },

    enter() {
      /* The element the reader opened from, read here rather than when the
         component was built: the trigger is whatever was clicked, and it did not
         exist at the moment this was created. */
      this.returnFocus = document.activeElement;

      /* The scroll position and the document's own overflow value are both
         remembered, because both are handed back rather than assumed. The
         overflow is restored to what it was rather than cleared, since something
         else on the page may have set it; the position is restored explicitly as
         well as preserved by the lock, because a browser that reflows the
         document when the root's overflow changes would otherwise leave the
         reader somewhere they did not choose. */
      this.scrollY = window.scrollY;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';

      window.addEventListener('keydown', this.onKeydown);

      /* The close button rather than the sheet: it is the first thing a reader
         wants to reach, and focusing an element that is only a container would
         announce the window without offering the way out of it. */
      this.$nextTick(() => {
        if (this.$refs.close) {
          this.$refs.close.focus();
        }
      });
    },

    leave() {
      window.removeEventListener('keydown', this.onKeydown);

      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }

      /* `_reset.scss` holds `scroll-behavior: auto` on the root and hands smooth
         scrolling to a tween, so this is a jump back to where the reader was
         rather than an animation of its own. */
      if (this.scrollY !== undefined) {
        window.scrollTo(0, this.scrollY);
        this.scrollY = undefined;
      }

      /* Focus goes back to the trigger, so a keyboard reader who opened the
         eighth shot returns to the eighth shot rather than to the top of the
         document. It is skipped when the trigger has gone, which is the case
         when the page itself is being unmounted. */
      if (this.returnFocus && this.returnFocus.isConnected && this.returnFocus.focus) {
        this.returnFocus.focus();
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();

        return;
      }

      if (event.key === 'ArrowLeft') {
        this.previous();

        return;
      }

      if (event.key === 'ArrowRight') {
        this.next();

        return;
      }

      if (event.key === 'Tab') {
        this.holdFocus(event);
      }
    },

    /* A window over the page holds the keyboard while it is open. Without this
       Tab would walk out of the viewer and into the page behind it, which is
       still in the document and still focusable, and the reader would be
       operating a page they cannot see.

       It wraps at the two ends rather than at a fixed order, so a click on a
       control part-way through the row still leaves Shift+Tab and Tab stepping
       through the three in the order they are drawn. */
    holdFocus(event) {
      const dialog = this.$refs.dialog;

      if (!dialog) {
        return;
      }

      const focusable = dialog.querySelectorAll(FOCUSABLE);

      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
};
</script>
